import {DateTime} from 'luxon';
import {useCallback} from 'react';
import {StartEndControllerTab} from '~/common/components/StartEndController/types';
import {DEFAULT_TIME_CONFIG} from '~/common/components/StartEndController/utils';
import {parseTime} from '~/common/utils';

type DateTimeChangePayload = {
  start: DateTime | null;
  end: DateTime | null;
  timezone: Timezone | null;
};

export const useDateTimeManagement = (
  start: DateTime | null,
  end: DateTime | null,
  tz: Timezone | null,
  selectedTab: StartEndControllerTab
) => {
  const autoPadEndDateByEventLength = useCallback(
    (payload: DateTimeChangePayload) => {
      if (!payload.start) return;
      if (start && end) {
        const eventLength = end.diff(start, ['hours', 'minutes']);
        payload.end = payload.start.plus(eventLength);
      } else {
        payload.end = payload.start.plus({hours: 3});
      }
    },
    [start, end]
  );

  const changeDate = useCallback(
    (date: Date): DateTimeChangePayload => {
      const payload: DateTimeChangePayload = {start, end, timezone: tz};

      let formattedDate = DateTime.fromJSDate(date);
      switch (selectedTab) {
        case 'start': {
          // Ensure the selected start time is on the date object
          formattedDate = formattedDate.set(
            start
              ? {
                  hour: start.hour,
                  minute: start.minute,
                  second: start.second,
                  millisecond: start.millisecond,
                }
              : DEFAULT_TIME_CONFIG
          );
          payload.start = formattedDate;

          // Ensure the end date is after the start date
          if (payload.end && payload.start.diff(payload.end).milliseconds > 0) {
            autoPadEndDateByEventLength(payload);
          }
          break;
        }
        case 'end': {
          // Ensure the selected end time is on the date object
          formattedDate = formattedDate.set({
            hour: end?.hour ?? 0,
            minute: end?.minute ?? 0,
            second: end?.second ?? 0,
            millisecond: end?.millisecond ?? 0,
          });
          payload.end = formattedDate;
          break;
        }
        default: {
          break;
        }
      }
      return payload;
    },
    [selectedTab, start, end, tz, autoPadEndDateByEventLength]
  );

  const changeTime = useCallback(
    (time: string): DateTimeChangePayload => {
      const payload: DateTimeChangePayload = {start, end, timezone: tz};

      if (!time) return payload;

      const match: RegExpMatchArray | null = parseTime(time);
      if (!match?.groups) return payload;

      // Extract hour, minute, and meridiem from the time string
      const {minute = '00', meridiem} = match.groups;
      let {hour} = match.groups;
      if (hour === '12' && meridiem === 'AM') hour = '00'; // Handle 12 AM case

      if (selectedTab === 'start') {
        let newStart = start ?? DateTime.now();
        let newHour = parseInt(hour, 10);
        if (meridiem === 'PM' && newHour !== 12) newHour += 12; // Convert PM hour to 24-hour format
        newStart = newStart.set({
          hour: newHour,
          minute: parseInt(minute, 10),
        });
        payload.start = newStart;

        // Ensure the end date is after the start date
        if (payload.end && payload.start.diff(payload.end).milliseconds > 0) {
          autoPadEndDateByEventLength(payload);
        }
      } else if (selectedTab === 'end') {
        let newEnd = end ?? DateTime.now();
        let newHour = parseInt(hour, 10);
        if (meridiem === 'PM' && newHour !== 12) newHour += 12; // Convert PM hour to 24-hour format
        newEnd = newEnd.set({
          hour: newHour,
          minute: parseInt(minute, 10),
        });
        payload.end = newEnd;
      }

      return payload;
    },
    [selectedTab, start, end, tz, autoPadEndDateByEventLength]
  );

  return {changeDate, changeTime};
};
